import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";
import { PageHeading } from "../MediaCentre/Media.hook";

interface Landing {
  id: number;
  title: string;
  field_content_english: {
    data: {
      cardData: {
        image: string;
        title: string;
        description: string;
        link: string;
      }[];
    };
    labelData: any;
  };
  field_content_malay: {
    data: {
      cardData: {
        image: string;
        title: string;
        description: string;
        link: string;
      }[];
    };
    labelData: any;
  };
  field_content_mandarin: {
    data: {
      cardData: {
        image: string;
        title: string;
        description: string;
        link: string;
      }[];
    };
    labelData: any;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}
interface PractisingSensibleEconomics {
  id: number;
  title: string;
  field_content_english: FieldContentEnglish;
  field_content_malay: FieldContentEnglish;
  field_content_mandarin: FieldContentEnglish;
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FieldContentEnglish {
  data: Data;
  labelData: any;
}

interface Data {
  features: Feature[];
  siderData: SiderData[];
  banner: string;
  title: string;
  introText: string;
  extroText: string;
  nextPage: NextPage;
}

interface Feature {
  key: string;
  image: string;
  title: string;
  description: string;
  color: string;
}

interface SiderData {
  key: string;
  image: string;
  title: string;
  description: string[];
}

export interface FTSE4Good {
  id: number;
  title: string;
  field_content_english: {
    data: {
      title: string;
      image: string;
      description: string[];
      nextPage: {
        name: string;
        link: string;
      };
    };
    labelData: any;
  };
  field_content_malay: {
    data: {
      title: string;
      image: string;
      description: string[];
      nextPage: {
        name: string;
        link: string;
      };
    };
    labelData: any;
  };
  field_content_mandarin: {
    data: {
      title: string;
      image: string;
      description: string[];
      nextPage: {
        name: string;
        link: string;
      };
    };
    labelData: any;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Report {
  id: number;
  image: string;
  link: string;
  title: string;
}

interface ReportFieldContentEnglish {
  data: ContentData;
  labelData: any;
}

interface ContentData {
  cardData: Report[];
  banner: string;
  nextPage: NextPage;
}

interface Reports {
  id: number;
  title: string;
  field_content_english: ReportFieldContentEnglish;
  field_content_malay: ReportFieldContentEnglish;
  field_content_mandarin: ReportFieldContentEnglish;
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export type Language = "field_content_english" | "field_content_malay" | "field_content_mandarin";

export interface NextPage {
  name: string;
  link: string;
}

export type NextPageKey = keyof NextPage;

const fetchData = async <T>(url: string): Promise<T> => {
  const response = await corporateCMSApi.get<T>(url);
  return response.data;
};

const createDataHook =
  <T>(key: string, url: string) =>
  (
    options?: UseQueryOptions<T, DefaultError, T, QueryKey>,
    queryClient?: QueryClient
  ): UseQueryResult<T, DefaultError> => {
    return useQuery<T, DefaultError, T, QueryKey>(
      {
        queryKey: [key],
        enabled: false,
        queryFn: () => fetchData<T>(url),
        refetchOnWindowFocus: false,
        ...options,
      },
      queryClient
    );
  };

const usePractisingSensibleEconomics = createDataHook<PractisingSensibleEconomics[]>(
  "practisingSensibleEconomics",
  `/api/page-content/all-fields?pageName=sustainability/practising-sensible-economics`
);

const useEnvironmentalConsciousness = createDataHook<PractisingSensibleEconomics[]>(
  "environmentalConsciousness",
  `/api/page-content/all-fields?pageName=sustainability/environmental-consciousness`
);

const useCreatingInspiringWorkplace = createDataHook<PractisingSensibleEconomics[]>(
  "creatingInspiringWorkplace",
  `/api/page-content/all-fields?pageName=sustainability/creating-inspiring-workplace`
);

const useCommunityFriendlyOrganisation = createDataHook<PractisingSensibleEconomics[]>(
  "communityFriendlyOrganisation",
  `/api/page-content/all-fields?pageName=sustainability/community-friendly-organisation`
);

const useMemorableAirportExperiences = createDataHook<PractisingSensibleEconomics[]>(
  "memorableAirportExperiences",
  `/api/page-content/all-fields?pageName=sustainability/memorable-airport-experiences`
);

const useFtse4Good = createDataHook<FTSE4Good[]>(
  "ftse4good",
  `/api/page-content/all-fields?pageName=sustainability/ftse4good`
);

const useLanding = createDataHook<Landing[]>("landing", `/api/page-content/all-fields?pageName=sustainability`);

const useReports = createDataHook<Reports[]>("reports", `/api/page-content/all-fields?pageName=sustainability/reports`);

const useLandingPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["landingPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const usePractisingSensibleEconomicsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["practisingSensibleEconomicsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability/practising-sensible-economics`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useEnvironmentalConsciousnessPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["environmentalConsciousnessPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability/environmental-consciousness`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useCreatingInspiringWorkplacePageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["creatingInspiringWorkplacePageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability/creating-inspiring-workplace`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useCommunityFriendlyOrganisationPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["communityFriendlyOrganisationPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability/community-friendly-organisation`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useMemorableAirportExperiencesPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["memorableAirportExperiencesPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability/memorable-airport-experiences`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useFtse4GoodPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["ftse4GoodPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability/ftse4good`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useReportsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["reportsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=sustainability/reports`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};
export {
  useCommunityFriendlyOrganisation,
  useCommunityFriendlyOrganisationPageHeading,
  useCreatingInspiringWorkplace,
  useCreatingInspiringWorkplacePageHeading,
  useEnvironmentalConsciousness,
  useEnvironmentalConsciousnessPageHeading,
  useFtse4Good,
  useFtse4GoodPageHeading,
  useLanding,
  useLandingPageHeading,
  useMemorableAirportExperiences,
  useMemorableAirportExperiencesPageHeading,
  usePractisingSensibleEconomics,
  usePractisingSensibleEconomicsPageHeading,
  useReports,
  useReportsPageHeading,
};
